import { useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api/ApiService";
import { ProfileWithSubscription } from "@/types/auth/auth";

interface UsersFilters {
  search?: string;
  role?: string;
  hasSubscription?: boolean;
  teamId?: number;
  needsAttention?: boolean;
}

export type UsersSortBy = "created_at" | "email" | "full_name";
export type UsersSortOrder = "asc" | "desc";

interface UsersQueryResult {
  profiles: ProfileWithSubscription[];
  count: number;
  /** Accounts with unbilled phone numbers, regardless of the other filters. */
  attentionCount: number;
}

export const useUsersWithSubscriptions = (
  filters: UsersFilters = {},
  page: number = 1,
  pageSize: number = 50,
  sortBy: UsersSortBy = "created_at",
  sortOrder: UsersSortOrder = "desc",
  enabled: boolean = true
) => {
  return useQuery<UsersQueryResult>({
    queryKey: ["users-with-subscriptions", filters, page, pageSize, sortBy, sortOrder],
    enabled,
    queryFn: async () => {
      const result = await ApiService.invoke<{
        profiles: ProfileWithSubscription[];
        count: number;
        limit: number;
        offset: number;
        attentionCount?: number;
      }>({
        resource: "profiles",
        action: "all",
        data: {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          sortBy,
          sortOrder,
          filters: {
            ...(filters.search && { searchTerm: filters.search }),
            ...(filters.role && { role: filters.role }),
            ...(filters.hasSubscription !== undefined && { hasSubscription: filters.hasSubscription }),
            ...(filters.teamId !== undefined && { teamId: filters.teamId }),
            ...(filters.needsAttention && { needsAttention: true }),
          },
        },
      });

      return {
        profiles: result?.profiles ?? [],
        count: result?.count ?? 0,
        attentionCount: result?.attentionCount ?? 0,
      };
    },
  });
};
